import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileCheck, Trash2, Upload } from 'lucide-react';
import { Card } from './ui/card';
import { Button } from './ui/button';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fileHref = (url) => `${process.env.REACT_APP_BACKEND_URL}${url}`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('pl-PL', { timeZone: 'Europe/Warsaw' }) : '');

export const ProjectProtocols = ({ projectId, onChange }) => {
  const [data, setData] = useState({ files: [], legacy: [], can_edit: false });
  const [busy, setBusy] = useState(false);
  const input = useRef(null);
  const load = useCallback(() => axios.get(`${API_URL}/projects/${projectId}/protocols`).then((r) => setData(r.data)).catch(() => {}), [projectId]);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) {
        const fd = new FormData();
        fd.append('file', f);
        await axios.post(`${API_URL}/projects/${projectId}/protocols`, fd);
      }
      toast.success('Protokół wgrany');
      load(); onChange?.();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Błąd wgrywania'); } finally { setBusy(false); }
  };
  const remove = async (p) => {
    if (!window.confirm(`Usunąć protokół „${p.original_filename}”?`)) return;
    try { await axios.delete(`${API_URL}/projects/${projectId}/protocols/${p.id}`); toast.success('Usunięto'); load(); onChange?.(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Błąd'); }
  };

  return (
    <Card id="protokol" className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6 scroll-mt-20" data-testid="project-protocol-section">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <h3 className="text-lg font-semibold text-[var(--crm-text-primary)] flex items-center gap-2"><FileCheck className="w-5 h-5 text-emerald-500" />Protokół odbioru</h3>
        {data.can_edit && (
          <>
            <input ref={input} type="file" accept=".pdf,.jpg,.jpeg,.png" multiple hidden onChange={upload} data-testid="protocol-file-input" />
            <Button size="sm" onClick={() => input.current?.click()} disabled={busy} className="bg-[#FF6B35] hover:bg-[#e55a2b] text-white" data-testid="protocol-upload-btn">
              <Upload className="w-4 h-4 mr-1" />{busy ? 'Wgrywanie...' : 'Wgraj protokół'}
            </Button>
          </>
        )}
      </div>
      {!data.files.length && !data.legacy.length && <p className="text-sm text-[var(--crm-text-muted)]" data-testid="protocol-empty">Brak protokołu odbioru.</p>}
      <ul className="space-y-2">
        {data.files.map((p) => (
          <li key={p.id} className="flex items-center gap-3 text-sm" data-testid={`protocol-file-${p.id}`}>
            <a href={fileHref(p.url)} target="_blank" rel="noopener noreferrer" className="text-[#FF6B35] hover:underline break-all">{p.original_filename}</a>
            <span className="text-xs text-[var(--crm-text-muted)]">{fmt(p.uploaded_at)} {p.uploaded_by_initials}</span>
            {data.can_edit && <button type="button" onClick={() => remove(p)} className="ml-auto text-red-400" data-testid={`protocol-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>}
          </li>
        ))}
        {data.legacy.map((t) => (
          <li key={t.id} className="flex items-center gap-3 text-sm" data-testid={`protocol-legacy-${t.id}`}>
            <a href={fileHref(t.url)} target="_blank" rel="noopener noreferrer" className="text-[#FF6B35] hover:underline">{t.title || 'Protokół (planer montaży)'}</a>
            <span className="text-xs text-[var(--crm-text-muted)]">archiwalny · {fmt(t.date)}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
};
